import '../IntegrationsTab.scss'
import React, { useMemo, useState } from 'react'
import { Col, Container, OverlayTrigger, Row, Stack, Tooltip } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'

import { useDeleteUserIntegrationMutation, useGetMeQuery, useGetUserIntegrationInfoQuery } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { TooltipProps } from 'react-bootstrap/Tooltip'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { createState, IntegrationServiceId } from '../../../../common/integrations'
import { Link } from 'react-router-dom'
import { getIntegrationRedirectUri } from '../../../Integration/utils'
import { IntegrationIcon } from '../../../../components/IntegrationIcon'

const ADMIN_AUTHORIZE_DOC_LINK = import.meta.env.REACT_APP_WEBEX_ADMIN_AUTHORIZE_DOC_LINK!

export const WebexIntegrationRow = () => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectAuthenticatedUser)
  const { data: userData, refetch: refetchUserData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const { data: userIntegrationInfoData } = useGetUserIntegrationInfoQuery({
    accountId: accountId,
    userId: currentUser!.id,
  })
  const [deleteUserIntegration, { isLoading: isDeletingIntegration }] = useDeleteUserIntegrationMutation()
  const webexIntegration = useMemo(() => {
    const user = userData
    if (!user || !user.integrations) {
      return null
    }
    return user.integrations.find((i) => i.serviceId === IntegrationServiceId.WEBEX)
  }, [userData])
  const [showProblemsTooltip, setShowProblemsTooltip] = useState(false)

  const authorizeWebex = () => {
    const redirectUri = getIntegrationRedirectUri(IntegrationServiceId.WEBEX)
    const state = createState(window.location.origin + window.location.pathname)
    window.location.href = `${import.meta.env.REACT_APP_IAM_API_BASE_URL}/accounts/${accountId}/user-integrations/${IntegrationServiceId.WEBEX}/enable?redirect_uri=${redirectUri}&state=${state}`
  }

  const uninstallWebex = () => {
    const user = userData
    const integration = user!.integrations!.find((i) => i.serviceId === IntegrationServiceId.WEBEX)
    deleteUserIntegration({
      accountId,
      payload: {
        userId: user!.id,
        integrationId: integration!.id,
      },
    })
  }
  const webexIntegrationProblems = webexIntegration
    ? (userIntegrationInfoData?.data.problems.filter((issue) => issue.integrationId === webexIntegration.id) ?? [])
    : []

  const renderWebexIntegrationProblemsTooltip = (props: TooltipProps) => (
    <Tooltip
      id='integration-info-tooltip'
      className='integration-info-tooltip'
      {...props}
      onMouseEnter={() => setShowProblemsTooltip(true)}
      onMouseLeave={() => setShowProblemsTooltip(false)}
    >
      <ul>
        {webexIntegrationProblems.map((problem) => {
          return (
            <>
              <li className='mb-2'>
                <b>{problem.issue}</b> {problem.resolution}
              </li>
            </>
          )
        })}
      </ul>
    </Tooltip>
  )

  return (
    <Container className='p-3 p-md-0 py-md-3 integration-container'>
      <Row className='d-flex justify-content-between'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div className='rounded py-1 px-1 zocks-bg-color-gray-100'>
              <IntegrationIcon serviceId={IntegrationServiceId.WEBEX} width={48} height={48} />
            </div>
            <div>
              <h6>Webex</h6>
              <div>
                Connect your Zocks account to Webex.
                <br />
                An admin may need to approve Zocks in Webex Control Hub. For more info, click{' '}
                <Link target='_blank' className='zocks-text-gray-900 text-decoration-underline' to={ADMIN_AUTHORIZE_DOC_LINK}>
                  here
                </Link>{' '}
                .
              </div>
            </div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-3 align-items-start'>
            {webexIntegration && (
              <div className='d-flex gap-3'>
                {webexIntegrationProblems.length ? (
                  <OverlayTrigger show={showProblemsTooltip} placement='top' overlay={renderWebexIntegrationProblemsTooltip}>
                    <div className='align-self-center' onMouseEnter={() => setShowProblemsTooltip(true)} onMouseLeave={() => setShowProblemsTooltip(false)}>
                      <i className='bi bi-exclamation-triangle zocks-text-warning-text' />
                    </div>
                  </OverlayTrigger>
                ) : null}
              </div>
            )}
            <ScopedBlock
              scope={{
                scope: 'integration:connect',
                superScope: ['integration:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <div className='connect-button-container'>
                {webexIntegration ? (
                  <div className='connect-button-container'>
                    <Button variant='primary' onClick={uninstallWebex}>
                      Disconnect
                    </Button>
                    <div className='pt-2 d-flex justify-content-end zocks-text-success'>
                      <i className='bi bi-check' />
                      Connected
                    </div>
                  </div>
                ) : (
                  <ScopedBlock
                    scope={{
                      scope: 'room_session:start',
                      superScope: ['room_session:admin', 'room:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Button variant='primary' onClick={authorizeWebex}>
                      Connect
                    </Button>
                  </ScopedBlock>
                )}
              </div>
            </ScopedBlock>
          </Stack>
        </Col>
      </Row>
    </Container>
  )
}
